import type { FormatHookParams } from '/business/shared/components/pages/question/types';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import useAbsoluteEmissions from '/business/shared/components/pages/question/formats/staticSharedFields/useAbsoluteEmissions';
import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useEmissionSourceDropdown from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeEmissionSourceDropdown';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import useMakeSupplierName from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeSupplierName';
import useMakeTravelDistance from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeTravelDistance';
import useVehicleSize from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeVehicleSize';
import {
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import getVehicleSizesSelector from '/business/shared/state/selectors/application/step1/getVehicleSizesSelector';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import {
  useGetDistanceDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';
import { useGetEmissionsFromQuestion } from '/business/shared/hooks/useGetEmissionsFromQuestion';
import useGetInitiativeTools from '/business/shared/hooks/useGetInitiativeTools';
import type { RootState } from '/business/shared/state/store';

export default function useFormatSharedTravelCarTaxiSupplier({
  question,
  questionFormatName,
  branchType,
  label,
  emissionSourcePlaceholder,
}: FormatHookParams) {
  const { getQuestionForInitiative } = useGetInitiativeTools();
  const [valueFuelType, setValueFuelType] = useState<string | null>(null);

  /*
  we inject in a question here, that HAS the fuel-type questionGroup (taxi is actually distance based, so we cannot load it)
   */
  let carQuestion = getQuestionForInitiative('calculation-travel-emissions-company-vehicles-company-owned-cars');
  if (!carQuestion) {
    carQuestion = getQuestionForInitiative('calculation-travelbytaxi');
  }

  const vehicleSizesFactory = useSelector((state: RootState) => getVehicleSizesSelector(state, { question }));
  const { mappedForDropdown: fuelTypes } = useGetEmissionsFromQuestion({
    question: carQuestion,
    questionStaticPropsOverrideApiKey: 'calculation-travelbycar-accurate',
    hideIfMissingVehicleSize: true,
  });

  const vehicleSizes = useMemo(() => {
    if (!vehicleSizesFactory) {
      return [];
    }
    return vehicleSizesFactory(valueFuelType);
  }, [valueFuelType]);

  //    TODO: [WR] we might need it for saving. Check after hooking the API
  // const vehicleSizeValues = useMemo(
  //   () =>
  //     [
  //       {
  //         name: null,
  //         isPlaceholder: true,
  //         displayNameKey: 'business:QuestionPage.forms.vehicleTypeSelector.selectVehicleSize',
  //       },
  //     ].concat(
  //       vehicleSizes.map(({ title, slug, name, subtitleName }) => {
  //         const optionTitle = t(`business:emissionFactors.vehicleSizes.${name}`, title);
  //         const optionSubtitle = t(`business:emissionFactors.vehicleSizesSubtitles.${subtitleName}`, subtitleName);

  //           const slugName = useSlugForSizeDropdown ? slug : title;
  //         return {
  //           name: slug,
  //           displayName: `${optionTitle} ${optionSubtitle}`,
  //         };
  //       }),
  //     ),
  //   [vehicleSizes, valueFuelType],
  // );

  const fuelType = useEmissionSourceDropdown({
    valuesArray: fuelTypes,
    emissionSourcePlaceholder: 'Select type of fuel',
    label: 'Fuel type:',
    errMsg: 'Please select a fuel type',
  });
  const vehicleSize = useVehicleSize(vehicleSizes);
  const supplierName = useMakeSupplierName({});
  const supplierIntensityData = useAbsoluteEmissions({
    propName: 'supplier_intensity_data',
    dropdownPropName: 'supplier_intensity_data_unit',
    label: 'Supplier intensity data:',
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(false),
    errMsg: 'Please enter supplier intensity data',
  });
  const absoluteEmissions = useAbsoluteEmissions({
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(true),
  });
  const distanceTravelled = useMakeTravelDistance({
    distanceDropdownValues: useGetDistanceDropdownValues(),
    label: 'Estimated distance traveled:',
  });
  const numOfVeh = useMakeQuantity({
    label: 'Number of vehicles of this size:',
    errMsg: 'Please enter a valid number of vehicles',
  });
  const howWasCollected = useHowWasCollected();

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Supplier intensity data
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received directly from suppliers based on carbon intensity/unit (e.g., kg CO
                  <sub>2</sub>e per units of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received from suppliers as a total sum (total kg or tonnes CO<sub>2</sub>e) in relation
                  to products or services your business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...fuelType,
        onChangeCallback: (e: string) => {
          setValueFuelType(e);
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...vehicleSize,
        isDisabled: !valueFuelType,
        //    TODO: [WR] we might need it for saving. Check after hooking the API
        // saveMeCallback: (fieldValue) => {
        //   const { inputValue: vehicleSlug } = fieldValue;
        //     if (!useSlugForSizeDropdown) {
        //       return {
        //         vehicleSize: vehicleSlug,
        //       };
        //     }
        //   const foundVehicle = vehicleSizes.find(({ slug }) => slug === vehicleSlug);
        //   return {
        //     vehicleSize: foundVehicle?.title,
        //     vehicleSizeName: foundVehicle?.name,
        //   };
        // },
      },

      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierIntensityData,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...distanceTravelled,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...numOfVeh,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...fuelType,
        onChangeCallback: (e: string) => {
          setValueFuelType(e);
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...vehicleSize,
        isDisabled: !valueFuelType,
        //    TODO: [WR] we might need it for saving. Check after hooking the API
        // saveMeCallback: (fieldValue) => {
        //   const { inputValue: vehicleSlug } = fieldValue;
        //     if (!useSlugForSizeDropdown) {
        //       return {
        //         vehicleSize: vehicleSlug,
        //       };
        //     }
        //   const foundVehicle = vehicleSizes.find(({ slug }) => slug === vehicleSlug);
        //   return {
        //     vehicleSize: foundVehicle?.title,
        //     vehicleSizeName: foundVehicle?.name,
        //   };
        // },
      },

      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...absoluteEmissions,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...distanceTravelled,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...numOfVeh,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },

      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, [label, emissionSourcePlaceholder, valueFuelType]);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
